import { Injectable } from '@angular/core';
import { environment } from '../../environments/environment';

// Prod-only APIs that must use prod's JWKS when running on staging
const prodJwksApis = ['admin.lokos.in', '/lokos-profile/federation/'];

interface JSONWebKey extends JsonWebKey {
    kid?: string;
    x5u?: string;
    x5c?: string[];
    x5t?: string;
}

interface JSONWebKeySet {
    keys: JSONWebKey[];
}

export interface ServerKey {
    publicKey: CryptoKey;
    kid?: string;
}

@Injectable({
    providedIn: 'root'
})
export class KeyService {
    private db: IDBDatabase;
    // Keyed by JWKS URL; caching the promise lets concurrent callers share a single fetch
    private readonly serverKeys = new Map<string, Promise<ServerKey>>();

    constructor() {
        this.initDB();
    }

    initDB() {
        return new Promise<IDBDatabase>((resolve, reject) => {
            const request = indexedDB.open('crypto-keys', 2);

            request.onupgradeneeded = () => {
                const db = request.result;
                db.createObjectStore('keys');
            };

            request.onsuccess = () => {
                this.db = request.result;
                this.generateKeys().catch(console.error);
                resolve(request.result);
            };

            request.onerror = () => reject(request.error);
        });
    }

    private async generateKeys() {
        const keyPair = await window.crypto.subtle.generateKey({ name: 'ECDH', namedCurve: 'P-256' }, false, [
            'deriveKey',
            'deriveBits'
        ]);
        const transaction = this.db.transaction(['keys'], 'readwrite');
        const store = transaction.objectStore('keys');
        store.put(keyPair, 'client-key');
    }

    async getClientKey() {
        return new Promise<CryptoKeyPair>((resolve, reject) => {
            const transaction = this.db.transaction(['keys'], 'readonly');
            const store = transaction.objectStore('keys');
            const request = store.get('client-key');

            request.onsuccess = () => {
                const result: CryptoKeyPair = request.result;
                resolve(result);
            };

            request.onerror = () => reject(request.error);
        });
    }

    getServerKey(requestUrl: string): Promise<ServerKey> {
        const jwksUrl = this.resolveJwksUrl(requestUrl);
        let serverKey = this.serverKeys.get(jwksUrl);
        if (!serverKey) {
            serverKey = this.fetchServerKey(jwksUrl).catch((error) => {
                this.serverKeys.delete(jwksUrl);
                throw error;
            });
            this.serverKeys.set(jwksUrl, serverKey);
        }
        return serverKey;
    }

    private resolveJwksUrl(requestUrl: string): string {
        const useProdJwks = !environment.production && prodJwksApis.some((pattern) => requestUrl.includes(pattern));
        return `${useProdJwks ? 'https://she-leaps.lokos.in' : ''}/api/crypto/jwks`;
    }

    private async fetchServerKey(jwksUrl: string): Promise<ServerKey> {
        const response = await fetch(jwksUrl);
        if (!response.ok || response.status !== 200) {
            throw new Error(`Fetching JSONWebKeySet error ${response.status} ${response.statusText}`);
        }
        const jwks: JSONWebKeySet = await response.json();
        const [jwk] = jwks.keys;
        const publicKey = await crypto.subtle.importKey('jwk', jwk, { name: 'ECDH', namedCurve: 'P-256' }, true, []);
        return { publicKey, kid: jwk.kid };
    }
}
